import React from "react";

import { Box, BoxSx, FlagIcon, Icon, Text } from "../../../foundations";
import { DropdownOption } from "./types";

interface DropdownItemProps {
  option: DropdownOption;
  isFocused: boolean;
  idx: number;
}

const renderIcon = (icon: DropdownOption["icon"]) => {
  if (!icon) {
    return null;
  }

  if (icon.type === "flag") {
    return <FlagIcon flag={icon.name} />;
  }

  return <Icon icon={icon.name} />;
};

export const DropdownItem: React.FC<DropdownItemProps> = ({
  option,
  isFocused,
  idx
}) => {
  const optionRef = React.useRef<HTMLLIElement>(null);

  // If options is focused but hidden by overflow,
  // scroll the dropdown to show the focused option
  React.useEffect(() => {
    if (isFocused && optionRef.current) {
      optionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest"
      });
    }
  }, [isFocused]);

  const { label, icon } = option;

  const dropdownItemContainerStyles: BoxSx = {
    cursor: "pointer",
    width: "100%",
    px: "spacing-5",
    py: "spacing-3",
    transition: "transition-color-medium",
    ":hover": {
      backgroundColor: "surface-brand"
    },
    ...(isFocused && {
      backgroundColor: "surface-brand"
    })
  };

  const dropdownItemStyles: BoxSx = {
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
    px: "spacing-4",
    py: "spacing-2"
  };

  return (
    <Box as="li" ref={optionRef}>
      <Box
        as="button"
        data-element-idx={idx}
        aria-selected={isFocused}
        role="option"
        tabIndex={-1}
        sx={dropdownItemContainerStyles}
      >
        <Box sx={dropdownItemStyles}>
          <Text variant="body-1-regular" sx={{ color: "currentColor" }}>
            {label}
          </Text>
          <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
            {renderIcon(icon)}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
